import { ChevronLeft, ChevronRight, X } from "lucide-react-native";
import { FC, useCallback, useEffect, useState } from "react";
import { Modal, Pressable } from "react-native";

import { useLocalization } from "@/hooks";
import { isVideoExtension } from "@/types/utils";
import { Box, Center, HStack, Image, Text } from "./base";
import VideoPlayer from "./VideoPlayer";

interface MediaGalleryProps {
  initialIndex?: number;
  media: string[];
  onClose: () => void;
  visible: boolean;
}

const getMediaType = (uri: string): "image" | "video" => {
  const pathWithoutQuery = uri.split("?")[0];
  const extension = pathWithoutQuery.split(".").pop()?.toLowerCase() || "";
  return isVideoExtension(extension) ? "video" : "image";
};

const MediaGallery: FC<MediaGalleryProps> = ({
  initialIndex = 0,
  media,
  onClose,
  visible,
}) => {
  const { i18n } = useLocalization();
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  // Reset the active index during render (not in an effect) when the gallery is
  // (re)opened or the initial index changes.
  const indexSyncKey = `${initialIndex}|${visible}`;
  const [prevIndexSyncKey, setPrevIndexSyncKey] = useState(indexSyncKey);
  if (prevIndexSyncKey !== indexSyncKey) {
    setPrevIndexSyncKey(indexSyncKey);
    setCurrentIndex(initialIndex);
  }

  const handlePrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : media.length - 1));
  }, [media.length]);

  const handleNext = useCallback(() => {
    setCurrentIndex((prev) => (prev < media.length - 1 ? prev + 1 : 0));
  }, [media.length]);

  useEffect(() => {
    if (!visible) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") {
        handlePrevious();
      } else if (event.key === "ArrowRight") {
        handleNext();
      } else if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleNext, handlePrevious, onClose, visible]);

  if (!visible || media.length === 0) return null;

  const currentUri = media[currentIndex];
  const mediaType = getMediaType(currentUri);
  const showNavigation = media.length > 1;

  return (
    <Modal
      animationType="fade"
      onRequestClose={onClose}
      transparent={true}
      visible={visible}
    >
      {/* Full screen pressable overlay */}
      <Pressable
        className="flex-1 bg-black/90"
        onPress={onClose}
        style={{ cursor: "pointer" }}
      >
        {/* Close button */}
        <Pressable
          className="absolute top-4 right-4 z-50 w-10 h-10 rounded-full bg-white/10 items-center justify-center"
          onPress={onClose}
          style={{
            cursor: "pointer",
            // @ts-expect-error Web-specific styles
            transition: "background-color 150ms ease-in-out",
          }}
        >
          <X color="rgb(var(--color-typography-0))" size={24} />
        </Pressable>

        {/* Media counter */}
        {showNavigation && (
          <Box className="absolute top-4 left-1/2 -translate-x-1/2 z-50">
            <Text className="text-white text-sm">
              {currentIndex + 1} / {media.length}
            </Text>
          </Box>
        )}

        {/* Main content */}
        <Center className="flex-1" style={{ pointerEvents: "box-none" }}>
          <Pressable
            className="w-full h-full max-w-[90vw] max-h-[90vh] items-center justify-center"
            onPress={(e) => e.stopPropagation()}
          >
            {mediaType === "video" ? (
              <VideoPlayer
                className="w-full h-full"
                showControls={true}
                uri={currentUri}
              />
            ) : (
              <Image
                alt={i18n.t("misc.selectedImageAlt", {
                  index: currentIndex + 1,
                })}
                className="w-full h-full"
                resizeMode="contain"
                size="none"
                source={{ uri: currentUri }}
              />
            )}
          </Pressable>
        </Center>

        {/* Navigation arrows */}
        {showNavigation && (
          <>
            {/* Previous button */}
            <Pressable
              className="absolute left-4 top-1/2 -translate-y-1/2 z-50 w-12 h-12 rounded-full bg-white/10 items-center justify-center"
              onPress={(e) => {
                e.stopPropagation();
                handlePrevious();
              }}
              style={{
                cursor: "pointer",
                // @ts-expect-error Web-specific styles
                transition: "background-color 150ms ease-in-out",
              }}
            >
              <ChevronLeft color="rgb(var(--color-typography-0))" size={28} />
            </Pressable>

            {/* Next button */}
            <Pressable
              className="absolute right-4 top-1/2 -translate-y-1/2 z-50 w-12 h-12 rounded-full bg-white/10 items-center justify-center"
              onPress={(e) => {
                e.stopPropagation();
                handleNext();
              }}
              style={{
                cursor: "pointer",
                // @ts-expect-error Web-specific styles
                transition: "background-color 150ms ease-in-out",
              }}
            >
              <ChevronRight color="rgb(var(--color-typography-0))" size={28} />
            </Pressable>
          </>
        )}

        {/* Thumbnail strip */}
        {showNavigation && (
          <HStack className="absolute bottom-6 left-1/2 -translate-x-1/2 z-50 gap-2 px-4 py-2 rounded-xl bg-black/50">
            {media.map((uri, index) => {
              const itemType = getMediaType(uri);
              const isActive = index === currentIndex;

              return (
                <Pressable
                  className={`w-12 h-12 rounded-lg overflow-hidden border-2 ${
                    isActive ? "border-white" : "border-transparent opacity-60"
                  }`}
                  key={index}
                  onPress={(e) => {
                    e.stopPropagation();
                    setCurrentIndex(index);
                  }}
                  style={{
                    cursor: "pointer",
                    // @ts-expect-error Web-specific styles
                    transition:
                      "opacity 150ms ease-in-out, border-color 150ms ease-in-out",
                  }}
                >
                  {itemType === "video" ? (
                    <Box className="w-full h-full bg-gray-800 items-center justify-center">
                      <Box className="w-0 h-0 border-l-8 border-l-white border-t-4 border-t-transparent border-b-4 border-b-transparent ml-1" />
                    </Box>
                  ) : (
                    <Image
                      alt={i18n.t("misc.selectedImageAlt", {
                        index: index + 1,
                      })}
                      className="w-full h-full"
                      resizeMode="cover"
                      size="none"
                      source={{ uri }}
                    />
                  )}
                </Pressable>
              );
            })}
          </HStack>
        )}
      </Pressable>
    </Modal>
  );
};

export default MediaGallery;
